import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import PatientSelector from '@/components/PatientSelector';
import FoodManagement from '@/components/FoodManagement';
import { Calendar as CalendarIcon, Edit } from 'lucide-react';
import { Calendar as CalendarComp } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { usePatientContext } from '@/contexts/PatientContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendar = ({ user, onLogout }) => {
  const { selectedPatientId, setSelectedPatientId } = usePatientContext();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationLogs, setMedicationLogs] = useState([]);
  const [criticalEvents, setCriticalEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    date: '',
    time: '',
    description: '',
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchCalendarData();
    }
  }, [selectedPatient]);

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSelectedPatientId(patient.id);
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(response.data);
      
      // Use the stored patient ID if available, otherwise use first patient
      if (response.data.length > 0) {
        if (selectedPatientId) {
          const storedPatient = response.data.find(p => p.id === selectedPatientId);
          if (storedPatient) {
            setSelectedPatient(storedPatient);
          } else {
            setSelectedPatient(response.data[0]);
            setSelectedPatientId(response.data[0].id);
          }
        } else {
          setSelectedPatient(response.data[0]);
          setSelectedPatientId(response.data[0].id);
        }
      }
    } catch (error) {
      toast.error('Failed to load patients');
    }
  };

  const fetchCalendarData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [appointmentsRes, symptomsRes, ceRes] = await Promise.all([
        axios.get(`${API}/appointments/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/symptoms/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/critical-events/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
      ]);

      setAppointments(appointmentsRes.data);
      setSymptoms(symptomsRes.data);
      setCriticalEvents(ceRes.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingAppointment) {
        // Update existing appointment
        await axios.put(
          `${API}/appointments/${editingAppointment.id}`,
          appointmentForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Appointment updated');
      } else {
        // Create new appointment
        await axios.post(
          `${API}/appointments`,
          { ...appointmentForm, patient_id: selectedPatient.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Appointment added');
      }
      
      setShowAddAppointment(false);
      setEditingAppointment(null);
      setAppointmentForm({ date: '', time: '', description: '' });
      fetchCalendarData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Appointments are a premium feature. Upgrade to access.');
      } else {
        toast.error(editingAppointment ? 'Failed to update appointment' : 'Failed to add appointment');
      }
    }
  };

  const getDayIndicators = (date) => {
    // Get date in local timezone to avoid offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const indicators = [];

    // Green for symptoms
    if (symptoms.some((s) => s.date === dateStr)) {
      indicators.push('symptom');
    }
    // Blue for appointments
    if (appointments.some((a) => a.date === dateStr)) {
      indicators.push('appointment');
    }
    // Red for critical events
    if (criticalEvents.some((e) => e.date === dateStr)) {
      indicators.push('critical');
    }

    return indicators;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingAppointments = appointments
    .filter((a) => new Date(a.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  const pastAppointments = appointments
    .filter((a) => new Date(a.date) < today)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10);

  if (!selectedPatient) {
    return (
      <div className="min-h-screen p-4">
        <Navigation active="calendar" />
        <div className="max-w-4xl mx-auto pt-8">
          <div className="flex justify-end mb-4">
            <PatientSelector
              patients={patients}
              selectedPatient={null}
              onSelectPatient={handlePatientSelect}
              onPatientsChange={fetchPatients}
            />
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">No patients found</h2>
            <p className="text-gray-600 mb-6">Click the + button above to add your first patient</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="calendar-page">
      <Navigation active="calendar" />

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#333' }}>Calendar</h1>
            <p className="text-gray-600 mt-1">Track appointments and health history</p>
          </motion.div>
          <PatientSelector
            patients={patients}
            selectedPatient={selectedPatient}
            onSelectPatient={handlePatientSelect}
            onPatientsChange={fetchPatients}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card lg:col-span-2"
            data-testid="calendar-widget"
          >
            <div className="flex justify-center">
              <CalendarComp
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md"
                modifiers={{
                  hasSymptom: (date) => getDayIndicators(date).includes('symptom'),
                  hasAppointment: (date) => getDayIndicators(date).includes('appointment'),
                  hasCritical: (date) => getDayIndicators(date).includes('critical'),
                }}
                modifiersClassNames={{
                  hasSymptom: 'bg-green-100',
                  hasAppointment: 'bg-blue-100',
                  hasCritical: 'bg-red-100',
                }}
              />
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#4ade80' }}></div>
                <span>Symptoms</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#60a5fa' }}></div>
                <span>Appointments</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: '#f87171' }}></div>
                <span>Critical Events</span>
              </div>
            </div>
          </motion.div>

          {/* Upcoming Appointments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
            data-testid="appointments-list"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold" style={{ color: '#A2DAD3' }}>Upcoming</h2>
              <Dialog open={showAddAppointment} onOpenChange={(open) => {
                setShowAddAppointment(open);
                if (!open) setEditingAppointment(null);
              }}>
                <DialogTrigger asChild>
                  <button
                    data-testid="add-appointment-button"
                    className="btn btn-primary text-sm px-4 py-2"
                  >
                    + Add
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingAppointment ? 'Edit Appointment' : 'Add Appointment'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddAppointment} className="space-y-4" data-testid="appointment-form">
                    <div>
                      <label className="block text-sm font-medium mb-2">Date</label>
                      <input
                        data-testid="appointment-date-input"
                        type="date"
                        value={appointmentForm.date}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Time</label>
                      <input
                        data-testid="appointment-time-input"
                        type="time"
                        value={appointmentForm.time}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        data-testid="appointment-description-input"
                        value={appointmentForm.description}
                        onChange={(e) => setAppointmentForm({ ...appointmentForm, description: e.target.value })}
                        rows="3"
                        required
                      />
                    </div>
                    <button data-testid="submit-appointment-button" type="submit" className="btn btn-primary w-full">
                      {editingAppointment ? 'Update Appointment' : 'Add Appointment'}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {upcomingAppointments.length === 0 ? (
              <p className="text-gray-500 text-center py-8 text-sm">No upcoming appointments</p>
            ) : (
              <div className="space-y-3 mb-6">
                {upcomingAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{appointment.description}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {appointment.date} at {appointment.time}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingAppointment(appointment);
                            setAppointmentForm({
                              date: appointment.date,
                              time: appointment.time,
                              description: appointment.description,
                            });
                            setShowAddAppointment(true);
                          }}
                          className="text-blue-500 hover:text-blue-700 p-1"
                          title="Edit appointment"
                        >
                          <Edit size={14} />
                        </button>
                        <CalendarIcon size={16} style={{ color: '#F9A7A7' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Past Appointments */}
            {pastAppointments.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-4"></div>
                <h3 className="text-sm font-semibold mb-3 text-gray-600">Past Appointments</h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {pastAppointments.map((appointment) => (
                    <div
                      key={appointment.id}
                      className="p-2 rounded-lg opacity-75"
                      style={{ backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5' }}
                      data-testid={`past-appointment-${appointment.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-xs text-gray-700">{appointment.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {appointment.date} at {appointment.time}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingAppointment(appointment);
                              setAppointmentForm({
                                date: appointment.date,
                                time: appointment.time,
                                description: appointment.description,
                              });
                              setShowAddAppointment(true);
                            }}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title="Edit appointment"
                          >
                            <Edit size={12} />
                          </button>
                          <CalendarIcon size={14} style={{ color: '#999' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Food Management Section */}
        <div className="mt-8">
          <FoodManagement patient={selectedPatient} />
        </div>

      </div>
    </div>
  );
};

export default Calendar;
